import { useEffect, useState } from "react";
import { Tag, Typography } from "antd";
import type { BattleManager } from "../../game/assets/pri_wqs/scripts/core/BattleManager";
import type {
  BattleRuntimeState,
  PropRuntimeEventType,
} from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import { getSandboxRarityLabel } from "./runtimeBridge";
import {
  StatusBar,
  StatusGroup,
} from "./styles";
import { getRarityPercentages, SANDBOX_RARITY_IDS } from "./choiceUtils";

const { Text } = Typography;

interface StatusViewState {
  rarityPercentages: number[];
  globalStatusItems: string[];
}

const EMPTY_STATUS: StatusViewState = {
  rarityPercentages: [0, 0, 0, 0],
  globalStatusItems: ["无全局效果"],
};

const STATUS_EVENTS: PropRuntimeEventType[] = [
  "gameStarted",
  "gameEnded",
  "commissionStarted",
  "commissionEnded",  "roundStarted",  "roundEnded",
  "magicChanged",
  "rarityWeightChanged",
  "permanentBoostRulesChanged",
  "commissionRoundLimitChanged",
  "propAcquired",
  "propRemoved",  "propEnteredBoard",
  "propLeftBoard",];

export function StatusPanel({ manager }: { manager?: BattleManager }) {
  const [{ rarityPercentages, globalStatusItems }, setStatus] = useState<StatusViewState>(EMPTY_STATUS);

  useEffect(() => {
    if (!manager) {
      setStatus(EMPTY_STATUS);
      return undefined;
    }

    const refresh = () => setStatus(readStatus(manager.getState()));
    refresh();
    const eventBus = manager.getEventBus();
    const unsubs = STATUS_EVENTS.map((type) => eventBus.on(type, () => refresh()));
    return () => {
      unsubs.forEach((unsubscribe) => unsubscribe());
    };
  }, [manager]);

  return (
    <StatusBar>
      <StatusGroup>
        <Text type="secondary">品质权重</Text>
        {rarityPercentages.map((value, index) => (
          <Tag key={SANDBOX_RARITY_IDS[index]} color={value > 0 ? "blue" : "default"}>
            {getSandboxRarityLabel(SANDBOX_RARITY_IDS[index])} {value.toFixed(1)}%
          </Tag>
        ))}
      </StatusGroup>
      <StatusGroup>
        <Text type="secondary">战场</Text>
        {globalStatusItems.map((item) => (
          <Tag key={item}>{item}</Tag>
        ))}
      </StatusGroup>
    </StatusBar>
  );
}

function readStatus(state: BattleRuntimeState): StatusViewState {
  return {
    rarityPercentages: getRarityPercentages(state),
    globalStatusItems: readGlobalStatusItems(state),
  };
}

function readGlobalStatusItems(state: BattleRuntimeState): string[] {
  const items = [
    `委托 ${state.commissionIndex}`,
    `轮次 ${state.roundIndex}`,
    `总轮 ${state.totalRoundIndex}`,
    `本轮 ${state.currentRoundMagic}`,
    `总魔力 ${state.totalMagic}`,
  ];
  if (state.extraCommissionRounds > 0) items.push(`调和 +${state.extraCommissionRounds}`);
  if (state.commissionOnceKeys.size > 0) items.push(`限次 ${state.commissionOnceKeys.size}`);
  if (state.permanentBoostRules.length > 0) items.push(`永久加成 ${state.permanentBoostRules.length}`);
  const roundBoostCount = Object.keys(state.roundYieldBoosts).length;
  if (roundBoostCount > 0) items.push(`本轮加成 ${roundBoostCount}`);
  if (items.length === 0) return ["无全局效果"];
  return items;
}
